import { z } from 'zod';

export const RAW_SELECTOR_PATTERN: RegExp = new RegExp(
  '^(?<childrens_parents>@)?\
(?<parents>(?<parents_depth>\\d*)\\+)?\
((?<method>[\\w.]+):)?(?<value>.*?)\
(?<children>\\+(?<children_depth>\\d*))?$',
);

type ParsedRegex = {
  childrens_parents: boolean;
  parents: boolean;
  parents_depth: number | null;
  method: string;
  value: string;
  children: boolean;
  children_depth: number | null;
};

type RelativePos = 'before_' | 'in_' | 'after_';

const distinctRegexSegments = [
  'childrens_parents',
  'parents',
  // 'parents_depth', // this is a part of `parents`
  'method',
  'value',
  'children',
  // 'children_depth', // this is a part of `children`
] as const satisfies readonly (keyof ParsedRegex)[];

type DistinctRegexSegment = (typeof distinctRegexSegments)[number];

export type RegexPart = `${RelativePos}${DistinctRegexSegment}`;

const getRegexSegmentLength = (
  groups: Record<string, string>,
  segment: DistinctRegexSegment,
): number | undefined => {
  const component = groups[segment];
  if (!component) {
    return undefined;
  }
  /**
   * The colon is the only character not in a capture group
   * And we can only match `method` if there is a colon
   */
  return component.length + (segment === 'method' ? 1 : 0);
};

/**
 * Given a selector and cursor position,
 * determine where the cursor is in the selector.
 */
export const getRegexPart = (selector: string, position: number): null | RegexPart => {
  if (!selector) {
    return 'after_value';
  }
  const groups = RAW_SELECTOR_PATTERN.exec(selector)?.groups;
  if (!groups) {
    return null;
  }
  if ([' ', ','].some((setOperator) => selector.includes(setOperator))) {
    return null;
  }

  if (!isNaN(Number(selector))) {
    if (position === 0) return 'before_parents';
    return 'in_parents';
  }

  let cumulativeLength = 0;
  let priorLength = 0;

  const relevantSegments = distinctRegexSegments.filter(
    (segment) => !!groups[segment]?.length || segment === 'value',
  );

  for (const segment of relevantSegments) {
    priorLength = cumulativeLength;
    cumulativeLength += getRegexSegmentLength(groups, segment) ?? 0;

    if (position > cumulativeLength) {
      continue;
    }
    if (position === cumulativeLength) {
      return `after_${segment}`;
    }
    if (position - priorLength === 0) {
      // Only return before if there's actually content
      return `before_${segment}`;
    }
    return `in_${segment}`;
  }

  return null;
};

export const methodNames = [
  'source',
  'resource_type',
  'path',
  'file',
  'access',
  'config',
  'tag',
  'fqn',
  'package',
  'test_type',
  'test_name',
  'state',
  'exposure',
  'metric',
  'result',
  'source_status',
  'group',
  // There is no such method as 'wildcard' in dbt-core
  // Multiple selectors support wildcard operations
  // https://github.com/dbt-labs/dbt-core/pull/9642
  // 'wildcard',
  'version',
  'semantic_model',
  'saved_query',
] as const;

export const stateOptions = [
  'new',
  'modified',
  'modified.body',
  'modified.configs',
  'modified.relation',
  'modified.persisted_descriptions',
  'modified.macros',
  'modified.contract',
] as const;

export type StateOption = (typeof stateOptions)[number];

export const accessTypes = ['private', 'protected', 'public'] as const;

export const versionValues = ['latest', 'prerelease', 'old', 'none'] as const;

export const zMethodName = z.enum(methodNames);

export type Method = (typeof methodNames)[number];

export type SingleSelector = {
  raw: string;
  method: Method;
  method_arguments: string[];
  value: string | undefined;
  children: boolean;
  childrens_parents: boolean;
  parents: boolean;
  parents_depth: number | null;
  children_depth: number | null;
  indirect_selection: 'eager';
};

/**
 * Takes in a raw selector string.
 * Returns the union of intersections of selectors.
 *
 * This logic aims to be a 1:1 representation of how dbt-core handles selector
 * syntax. See [syncing with core](../docs/syncingWithCore.md) for more info.
 */
export const zParsedSelectorString = z.string().transform((str, ctx) => {
  let index = 0;
  const unionsOfIntersections = str
    .split(' ')
    .map((union) => union.split(','))
    .map((intersection) =>
      intersection.map((singleSelector) => ({
        regex: RAW_SELECTOR_PATTERN.exec(singleSelector),
        raw: singleSelector,
      })),
    );
  return unionsOfIntersections.map((union) => {
    return union.map(({ regex, raw }): SingleSelector => {
      const startIndex = index;
      const endIndex = index + raw.length;
      const path = [startIndex, endIndex];
      index = endIndex + 1;
      if (!regex || !regex.groups) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Invalid selector spec "${raw}"`,
          path,
        });
        return z.NEVER;
      }
      const groups = regex.groups as Record<keyof ParsedRegex, string | undefined>;
      const parents_depth = groups.parents_depth
        ? parseInt(groups.parents_depth)
        : null;
      if (parents_depth && isNaN(parents_depth)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Invalid parents_depth in "${raw}"`,
          path: [path[0], path[0] + `${parents_depth}`.length],
        });
        return z.NEVER;
      }
      const children_depth = groups.children_depth
        ? parseInt(groups.children_depth)
        : null;
      if (children_depth && isNaN(children_depth)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Invalid children_depth in "${raw}"`,
          path: [path[1] - `${children_depth}`.length, path[1]],
        });
        return z.NEVER;
      }
      const value = groups.value;
      let methodGroup = groups.method;
      if (!methodGroup) {
        if (value?.includes('/')) {
          methodGroup = 'path';
        } else {
          methodGroup = 'fqn';
        }
      }
      const childrens_parents = !!groups.childrens_parents;
      const children = !!groups.children;
      const parents = !!groups.parents;

      const [rawMethod, ...method_arguments] = methodGroup.split('.');
      const parsedMethod = zMethodName.safeParse(rawMethod);
      if (!parsedMethod.success) {
        const startChar = path[0] + raw.indexOf(rawMethod);
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `'${methodGroup}' is not a valid method name`,
          path: [startChar, startChar + rawMethod.length],
        });
        return z.NEVER;
      }

      return {
        raw,
        method: parsedMethod.data,
        method_arguments,
        value,
        children,
        childrens_parents,
        parents,
        parents_depth,
        children_depth,
        indirect_selection: 'eager' as const,
      };
    });
  });
});
